import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Plus, ChevronLeft, ChevronRight, Search, Users, Mail } from 'lucide-react';
import { useEmployees, useCountries, useDepartments } from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const AVATAR_COLORS = [
  'from-primary to-primary-light',
  'from-accent to-green-400',
  'from-warning to-yellow-400',
  'from-error to-pink-400',
  'from-info to-cyan-400',
  'from-purple-500 to-indigo-400',
];

const STATUS_MAP: Record<
  string,
  { variant: 'success' | 'neutral' | 'warning' | 'error'; label: string }
> = {
  ACTIVE: { variant: 'success', label: 'Activo' },
  INACTIVE: { variant: 'neutral', label: 'Inactivo' },
  ON_LEAVE: { variant: 'warning', label: 'En licencia' },
  TERMINATED: { variant: 'error', label: 'Dado de baja' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_MAP[status] ?? { variant: 'neutral' as const, label: status };
  return (
    <Badge variant={s.variant} dot>
      {s.label}
    </Badge>
  );
}

export function EmployeesPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canCreate = role === 'ADMIN' || role === 'HR';
  const reduce = useReducedMotion();
  const [search, setSearch] = useState('');
  const [countryId, setCountryId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading } = useEmployees({
    search: search || undefined,
    countryId: countryId || undefined,
    departmentId: departmentId || undefined,
    status: status || undefined,
    page,
    pageSize,
  });
  const { data: countries } = useCountries();
  const { data: departments } = useDepartments();

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIdx = (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Header con acento de marca */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
            <Users size={22} className="text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-font-primary">Colaboradores</h1>
            <p className="mt-0.5 text-sm text-font-secondary">
              Gestiona el talento humano de ISOFT
            </p>
          </div>
        </div>
        {canCreate && (
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="secondary" size="lg">
              <Link to="/colaboradores/difusiones">
                <Mail size={16} />
                Difusiones
              </Link>
            </Button>
            <Button asChild variant="gradient" size="lg">
              <Link to="/colaboradores/nuevo">
                <Plus size={16} />
                Nuevo colaborador
              </Link>
            </Button>
          </div>
        )}
      </div>

      <FadeIn delay={0.05}>
        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
            <select
              value={countryId}
              onChange={(e) => {
                setCountryId(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">País: Todos</option>
              {countries?.items?.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.nameEs}
                </option>
              ))}
            </select>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Área: Todas</option>
              {departments?.items?.map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-md border border-border bg-white px-3 text-sm text-font-primary transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">Estado: Todos</option>
              <option value="ACTIVE">Activo</option>
              <option value="INACTIVE">Inactivo</option>
              <option value="ON_LEAVE">En licencia</option>
              <option value="TERMINATED">Dado de baja</option>
            </select>

            <div className="ml-auto flex h-9 w-full max-w-[16rem] items-center gap-2 rounded-md border border-border px-3 transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
              <Search size={15} className="text-font-tertiary" />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Buscar..."
                className="flex-1 bg-transparent text-sm placeholder:text-font-tertiary focus:outline-none"
              />
            </div>
          </div>

          {/* Tabla */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
                  <th className="px-6 py-3">Nombre</th>
                  <th className="px-4 py-3">Puesto</th>
                  <th className="px-4 py-3">Área</th>
                  <th className="px-4 py-3">País</th>
                  <th className="px-4 py-3">Jefe</th>
                  <th className="px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {isLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="border-b border-border-light">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full brand-shimmer" />
                          <div className="space-y-2">
                            <div className="h-3 w-32 rounded brand-shimmer" />
                            <div className="h-2.5 w-20 rounded brand-shimmer" />
                          </div>
                        </div>
                      </td>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-4 py-4">
                          <div className="h-3 w-16 rounded brand-shimmer" />
                        </td>
                      ))}
                    </tr>
                  ))}

                <AnimatePresence mode="popLayout">
                  {!isLoading &&
                    data?.items?.map((e: any, i: number) => {
                      const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
                      const fullName = `${e.firstName} ${e.lastName}`;
                      return (
                        <motion.tr
                          key={e.id}
                          layout
                          initial={reduce ? false : { opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{
                            duration: 0.35,
                            ease: [0.22, 1, 0.36, 1],
                            delay: reduce ? 0 : Math.min(i * 0.035, 0.3),
                          }}
                          className="group border-b border-border-light transition-colors hover:bg-primary-subtle/40"
                        >
                          <td className="px-6 py-4">
                            <Link
                              to={`/colaboradores/${e.id}`}
                              className="flex items-center gap-3"
                            >
                              <div
                                className={`flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br ${color} text-[11px] font-semibold text-white shadow-sm transition-transform group-hover:scale-105`}
                              >
                                {initials(fullName)}
                              </div>
                              <div>
                                <p className="font-semibold text-font-primary transition-colors group-hover:text-primary">
                                  {fullName}
                                </p>
                                <p className="text-xs text-font-tertiary">
                                  {e.corporateEmail ?? e.employeeNumber}
                                </p>
                              </div>
                            </Link>
                          </td>
                          <td className="px-4 py-4 text-font-secondary">
                            {e.position?.title ?? '–'}
                          </td>
                          <td className="px-4 py-4 text-font-secondary">
                            {e.department?.name ?? '–'}
                          </td>
                          <td className="px-4 py-4 text-font-secondary">
                            {e.country?.code ?? '–'}
                          </td>
                          <td className="px-4 py-4 text-font-secondary">
                            {e.manager
                              ? `${e.manager.firstName} ${e.manager.lastName}`
                              : '–'}
                          </td>
                          <td className="px-4 py-4">
                            <StatusBadge status={e.status} />
                          </td>
                        </motion.tr>
                      );
                    })}
                </AnimatePresence>

                {!isLoading && data?.items?.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bg">
                          <Users size={22} className="text-font-tertiary" />
                        </span>
                        <p className="text-sm font-medium text-font-secondary">
                          Sin resultados
                        </p>
                        <p className="text-xs text-font-tertiary">
                          Ajusta los filtros o la búsqueda.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div className="flex flex-wrap items-center justify-between gap-y-2 border-t border-border px-6 py-3">
            <p className="text-xs text-font-secondary">
              Mostrando {total === 0 ? 0 : startIdx} a {endIdx} de {total} colaboradores
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:bg-bg hover:text-primary disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1).map(
                (p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold transition ${
                      p === page
                        ? 'bg-primary text-white shadow-sm'
                        : 'border border-border text-font-secondary hover:bg-bg'
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:bg-bg hover:text-primary disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </FadeIn>
    </PageTransition>
  );
}
